import React, {FC, PropsWithChildren} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {Pill} from '../../pill/pill';
import {MultilineTooltipOverflowCondition} from '../tooltips/multilineTooltipOverflowCondition';

/*
 * Props.
 */

interface ApplicationCardProps {
  className?: string;
  avatar: React.ReactNode;
  name: React.ReactNode;
  description: React.ReactNode;
  children?: React.ReactNode;

  isEnabled?: boolean;
  isPinned?: boolean;
  isUpsellRequired?: boolean;
  onPinnedChange?: Handler;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  gap: 8px;
  padding: 20px;
  border: 1px solid ${(p) => p.theme.alphas.black20};
  ${(p) => boxShadow(p.theme, `0px 1px 9px ${p.theme.alphas.black20}`)};
  border-radius: 16px;
`;

const StyledApplicationCardHeaderDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
  line-height: 30px;
  overflow: hidden;
  align-items: center;
`;

const StyledHeaderAvatarDiv = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  overflow: hidden;
  box-sizing: border-box;
`;

const StyledHeaderNameDiv = styled.div`
  flex: 1;
  font-weight: ${fontWeights.bold};
  overflow: hidden;
`;

const StyledDescriptionWrapperDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  line-height: 18px;
  overflow: hidden;
  flex: 0 0 36px;
`;

const StyledChildrenWrapperDiv = styled.div`
  display: flex;
  margin-top: 8px;

  &:empty {
    display: none;
  }
`;

/*
 * Component.
 */

export const ApplicationCard: FC<PropsWithChildren<ApplicationCardProps>> = (props) => {
  const {
    className,
    avatar,
    name,
    description,
    isPinned,
    isEnabled,
    children,
    isUpsellRequired,
    onPinnedChange,
  } = props;
  return (
    <StyledWrapperDiv className={className}>
      <StyledApplicationCardHeaderDiv>
        <StyledHeaderAvatarDiv>{avatar}</StyledHeaderAvatarDiv>
        <StyledHeaderNameDiv>
          <TooltipCoordinator render={() => name}>
            <TooltipOverflowCondition>{name}</TooltipOverflowCondition>
          </TooltipCoordinator>
        </StyledHeaderNameDiv>
        {maybeRenderPinnedButton(isPinned, onPinnedChange)}
        {maybeRenderIsEnabledPill(isEnabled, isUpsellRequired)}
        {maybeRenderUpsellIcon(isUpsellRequired)}
      </StyledApplicationCardHeaderDiv>
      <StyledDescriptionWrapperDiv>
        <TooltipCoordinator render={() => description}>
          <MultilineTooltipOverflowCondition lines={2}>{description}</MultilineTooltipOverflowCondition>
        </TooltipCoordinator>
      </StyledDescriptionWrapperDiv>
      <StyledChildrenWrapperDiv>{children}</StyledChildrenWrapperDiv>
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderPinnedButton(isPinned?: boolean, onPinnedChange?: Handler) {
  if (!onPinnedChange) {
    return null;
  }
  return <IconButton isActive={isPinned} iconName="pinFilledSmall" label={null} onClick={onPinnedChange} />;
}

function maybeRenderIsEnabledPill(isEnabled?: boolean, isUpsellRequired?: boolean) {
  if (!isEnabled || isUpsellRequired) {
    return null;
  }
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Pill
          backgroundColor={theme.palette.green.shade20}
          color={theme.palette.green.shade40}
          size={VisualSizesEnum.MEDIUM}
        >
          <Trans id="application-card-enabled-pill">Enabled</Trans>
        </Pill>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderUpsellIcon(isUpsellRequired?: boolean) {
  if (!isUpsellRequired) {
    return null;
  }
  return (
    <UserThemeConsumer>
      {(theme) => <Icon name="diamondSmall" size={20} color={theme.palette.yellow.shade40} />}
    </UserThemeConsumer>
  );
}
